import { addDays, isValid, parse, parseISO } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"

const dollarUS = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  useGrouping: true,
  maximumFractionDigits: 0,
})

export const dateFormat = (
  value: string,
  outformat = "MM/dd/yyyy",
  inputFormat: string | null = null,
  dayCount?: number,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => {
  if (!value || value === "-") {
    return ""
  }
  if (inputFormat && !dayCount) {
    const parsed = parse(value, inputFormat, new Date(0))
    if (isNaN(parsed.getTime())) {
      const iso = parseISO(value)
      if (!isValid(iso)) return value
      return formatInTimeZone(iso, timezone, outformat)
    }
    const utcDate = new Date(
      Date.UTC(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
        parsed.getHours(),
        parsed.getMinutes(),
        parsed.getSeconds(),
      ),
    )
    return formatInTimeZone(utcDate, timezone, outformat)
  } else if (dayCount) {
    const iso = parseISO(value)
    if (!isValid(iso)) return value
    return formatInTimeZone(addDays(iso, dayCount), timezone, outformat)
  } else {
    const iso = parseISO(value)
    if (!isValid(iso)) return value
    return formatInTimeZone(iso, timezone, outformat)
  }
}

export const formatCurrency = (
  value: number,
  currency?: string,
  countryCode?: string,
) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "-"
  }
  const lanCode = countryCode ? "en-" + countryCode?.toLowerCase() : "en-us"
  const currencyValue = currency ? currency : "USD"
  const dollar = Intl.NumberFormat(lanCode, {
    style: "currency",
    currency: currencyValue,
    useGrouping: false,
    // eneble decimal  if no zero value in the  decimal and without round decimal
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    // eneble decimal  if no zero value in the  decimal and round decimal
    // minimumSignificantDigits: 2,
    // maximumSignificantDigits: 2,
  })
  return dollar.format(value)
}

export const formatCurrencyWithDecimals = (value: number) => {
  const dollar = Intl.NumberFormat("en-us", {
    style: "currency",
    currency: "USD",
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return dollar.format(value)
}

export const convertMBtoKB = (mb: number) => {
  return mb * 1024 // 1 MB = 1024 KB
}

export const checkIfObjectHasKeys = (obj: object) => {
  return Object.keys(obj).length
}

export const parseToObject = (value: any) => {
  let parsedValue = value

  while (typeof parsedValue === "string") {
    try {
      parsedValue = JSON.parse(parsedValue)
    } catch (error) {
      return null
    }
  }

  if (typeof parsedValue === "object" && parsedValue !== null) {
    return parsedValue
  } else {
    return null
  }
}

export const convertSummaryValue = (
  value: string,
  suffix: string,
  prefix = "",
) => {
  return prefix + value + suffix
}

export const formatAddress = (obj) => {
  let address = obj?.line_1
  if (obj?.line_2) {
    address = address.concat(", ", obj?.line_2)
  }
  if (obj?.city) {
    address = address.concat(", ", obj?.city)
  }
  if (obj?.state) {
    address = address.concat(", ", obj?.state)
  }
  if (obj?.countryName) {
    address = address.concat(", ", obj?.countryName)
  }
  if (obj?.zipCode) {
    address = address.concat(", ", obj.zipCode)
  }
  return address
}

export const getRandomInt = (max: number): number => {
  const array = new Uint32Array(1)
  window.crypto.getRandomValues(array)
  return array[0] % max
}
